/**
 * Robust clipboard helper.
 *
 * Tries the modern async Clipboard API first (only available on secure
 * contexts in most browsers). Falls back to the legacy
 * `document.execCommand('copy')` flow with a hidden textarea, which still
 * works in many iframe/embedded preview environments, mobile Safari
 * without explicit permission, and older browsers.
 *
 * Returns true on success, false if both paths fail — callers are
 * responsible for surfacing a manual-copy fallback (e.g. a modal with a
 * selectable input).
 */
export async function copyToClipboard(text) {
  if (typeof text !== "string") return false;

  // 1) Modern async Clipboard API.
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function" &&
      // Some browsers reject silently outside secure contexts.
      (typeof window === "undefined" || window.isSecureContext !== false)
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path.
  }

  // 2) Legacy fallback via hidden textarea + execCommand('copy').
  try {
    if (typeof document === "undefined") return false;
    const ta = document.createElement("textarea");
    ta.value = text;
    // Hide off-screen but keep selectable.
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "-9999px";
    ta.style.opacity = "0";
    ta.style.pointerEvents = "none";
    document.body.appendChild(ta);

    // iOS Safari needs an actual range selection.
    const prevSelection = document.getSelection()?.rangeCount
      ? document.getSelection().getRangeAt(0)
      : null;
    ta.focus();
    ta.select();
    try {
      ta.setSelectionRange(0, text.length);
    } catch {
      // Some browsers throw on setSelectionRange for textarea, ignore.
    }

    const ok = document.execCommand("copy");
    document.body.removeChild(ta);

    // Restore any selection the user had.
    if (prevSelection) {
      const sel = document.getSelection();
      sel.removeAllRanges();
      sel.addRange(prevSelection);
    }

    return !!ok;
  } catch {
    return false;
  }
}
